import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useHistory, useLocation } from 'react-router';

export function useQueryParams() {
  const history = useHistory();
  const { search } = useLocation();
  const searchRef = useRef(search);

  useEffect(() => {
    searchRef.current = search;
  }, [search]);

  const params = useMemo(() => new URLSearchParams(search), [search]);

  const addParams = useCallback(
    (newParams: Record<string, string | number>) => {
      const params = new URLSearchParams(searchRef.current);
      Object.entries(newParams).forEach(([key, value]) =>
        params.set(key, value.toString()),
      );
      replaceParams(params, history);
    },
    [history],
  );

  const removeParams = useCallback(
    (removeParams: string[]) => {
      const params = new URLSearchParams(searchRef.current);
      removeParams.forEach(p => params.delete(p));
      replaceParams(params, history);
    },
    [history],
  );

  return {
    params,
    addParams,
    removeParams,
  };
}

const replaceParams = (
  params: URLSearchParams,
  history: ReturnType<typeof useHistory>,
) => {
  const paramsStr = params.toString();
  const cleanPath = `${window.location.pathname}${paramsStr ? `?${paramsStr}` : ''}`;
  history.replace(cleanPath);
};
